import React, { useState, useMemo, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { PageHeader } from '@/components/layout'
import { useSubmissions, useGradeSubmission, useDeleteSubmission, useReturnSubmission, useAllAssignments } from '@/hooks/useAssignments'
import { useAssignment } from '@/hooks/useAssignments'
import { GradeSubmissionModal } from '@/components/assignments/GradeSubmissionModal'
import type { AssignmentSubmission, AssignmentWithLessonInfo } from '@/types/assignment'
import { useLesson, useModule, useCourse } from '@/hooks'
import { useAuth } from '@/hooks/useAuth'

type SortOption = 'newest' | 'oldest' | 'name_asc' | 'name_desc'

export const InstructorAssignmentSubmissionsPage: React.FC = () => {
  const navigate = useNavigate()
  const { user } = useAuth()
  const { assignmentId } = useParams<{ assignmentId: string }>()

  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin'
  const overviewPath = isAdmin ? '/admin' : '/instructor'

  const [sortBy, setSortBy] = useState<SortOption>('newest')
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [selectedSubmission, setSelectedSubmission] = useState<AssignmentSubmission | null>(null)
  const [showGradingModal, setShowGradingModal] = useState(false)
  const [viewMode, setViewMode] = useState<'courses' | 'assignments'>('courses')
  const [selectedCourseId, setSelectedCourseId] = useState<string | null>(null)

  const isPickerMode = !assignmentId

  // Reset course selection when navigating back to assignment list (no assignmentId)
  useEffect(() => {
    if (!assignmentId) {
      setViewMode('courses')
      setSelectedCourseId(null)
      setSearchQuery('')
    }
  }, [assignmentId])

  const { data: allAssignments, isLoading: assignmentsLoading } = useAllAssignments()
  const { data: assignment, isLoading: assignmentLoading } = useAssignment(assignmentId || '')
  const { data: submissions, isLoading: submissionsLoading } = useSubmissions(assignmentId || '')
  const gradeSubmission = useGradeSubmission()
  const deleteSubmission = useDeleteSubmission()
  const returnSubmission = useReturnSubmission()

  const { data: lesson } = useLesson(assignment?.lessonId || '')
  const { data: module } = useModule(lesson?.moduleId || '')
  const { data: course } = useCourse(module?.courseId || '')

  const courseBuilderPath = isAdmin
    ? `/admin/courses/${module?.courseId}/builder`
    : `/instructor/courses/${module?.courseId}/modules`
  const moduleEditorPath = isAdmin
    ? `/admin/modules/${module?.id}/lessons`
    : `/instructor/modules/${module?.id}/edit`

  const coursesMap = useMemo(() => {
    if (!allAssignments) return []
    const map = new Map<string, { courseId: string; courseTitle: string; assignments: AssignmentWithLessonInfo[]; totalPending: number }>()
    for (const a of allAssignments) {
      const key = a.courseId || 'unknown'
      if (!map.has(key)) {
        map.set(key, { courseId: key, courseTitle: a.courseTitle || 'Unknown Course', assignments: [], totalPending: 0 })
      }
      const entry = map.get(key)!
      entry.assignments.push(a)
      entry.totalPending += a.pendingCount
    }
    return Array.from(map.values()).sort((a, b) => b.totalPending - a.totalPending || a.courseTitle.localeCompare(b.courseTitle))
  }, [allAssignments])

  const courseAssignments = useMemo(() => {
    if (!allAssignments || !selectedCourseId) return []
    let list = allAssignments.filter((a) => a.courseId === selectedCourseId)
    if (searchQuery) {
      const q = searchQuery.toLowerCase()
      list = list.filter(
        (a) =>
          a.title.toLowerCase().includes(q) ||
          a.lessonTitle.toLowerCase().includes(q) ||
          a.moduleTitle.toLowerCase().includes(q)
      )
    }
    switch (sortBy) {
      case 'newest':
        return list.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
      case 'oldest':
        return list.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
      case 'name_asc':
        return list.sort((a, b) => a.title.localeCompare(b.title))
      case 'name_desc':
        return list.sort((a, b) => b.title.localeCompare(a.title))
      default:
        return list
    }
  }, [allAssignments, selectedCourseId, searchQuery, sortBy])

  const handleGrade = (submission: AssignmentSubmission) => {
    setSelectedSubmission(submission)
    setShowGradingModal(true)
  }

  const handleSaveGrade = async (score: number, feedback: string) => {
    if (!selectedSubmission || !assignmentId) return
    try {
      await gradeSubmission.mutateAsync({
        submissionId: selectedSubmission.id,
        assignmentId,
        data: { score, feedback },
      })
      setShowGradingModal(false)
      setSelectedSubmission(null)
    } catch (error) {
      console.error('Failed to grade submission:', error)
    }
  }

  const handleDelete = async (submissionId: string) => {
    if (!confirm('Are you sure you want to delete this submission?')) return
    if (!assignmentId) return
    try {
      await deleteSubmission.mutateAsync({ submissionId, assignmentId })
    } catch (error) {
      console.error('Failed to delete submission:', error)
    }
  }

  const handleReturn = async (submissionId: string) => {
    const feedback = prompt('Masukkan alasan pengembalian (wajib):')
    if (feedback === null) return
    if (!feedback.trim()) {
      alert('Alasan pengembalian wajib diisi.')
      return
    }
    if (!assignmentId) return
    try {
      await returnSubmission.mutateAsync({ submissionId, assignmentId, feedback: feedback.trim() })
    } catch (error) {
      console.error('Failed to return submission:', error)
    }
  }

  const selectedCourseName = coursesMap.find((c) => c.courseId === selectedCourseId)?.courseTitle || 'Course'

  // ========== DETAIL MODE ==========
  if (!isPickerMode) {
    const allSubmissions = submissions || []
    const filteredSubmissions = useMemo(() => {
      const filtered = statusFilter === 'all'
        ? allSubmissions
        : allSubmissions.filter(s => s.status === statusFilter)
      const list = [...filtered]
      switch (sortBy) {
        case 'newest':
          return list.sort((a, b) => new Date(b.submittedAt || 0).getTime() - new Date(a.submittedAt || 0).getTime())
        case 'oldest':
          return list.sort((a, b) => new Date(a.submittedAt || 0).getTime() - new Date(b.submittedAt || 0).getTime())
        case 'name_asc':
          return list.sort((a, b) => (a.studentName || '').localeCompare(b.studentName || ''))
        case 'name_desc':
          return list.sort((a, b) => (b.studentName || '').localeCompare(a.studentName || ''))
        default:
          return list
      }
    }, [allSubmissions, statusFilter, sortBy])

    const pendingSubmissions = allSubmissions.filter(s => s.status === 'submitted')
    const gradedSubmissions = allSubmissions.filter(s => s.status === 'graded')

    if (assignmentLoading || submissionsLoading) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="size-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-sm font-bold text-slate-500 dark:text-slate-400">Loading submissions...</p>
          </div>
        </div>
      )
    }

    if (!assignment) {
      return (
        <div className="rounded-2xl bg-rose-500/10 border border-rose-500/20 p-8 text-center max-w-md mx-auto mt-12">
          <span className="material-symbols-outlined text-rose-500 text-5xl mb-4">error</span>
          <p className="text-rose-600 dark:text-rose-400 font-black text-lg">Assignment Tidak Ditemukan</p>
        </div>
      )
    }

    return (
      <div className="max-w-[1600px] mx-auto">
        <PageHeader
          title={assignment.title}
          subtitle="Review dan nilai submission peserta"
          badge={`${assignment.maxScore} pts • ${assignment.passingScore}% passing`}
          badgeVariant="purple"
          breadcrumbs={
            isAdmin
              ? [
                  { label: 'Assignment Reviews', to: `${overviewPath}/assignments` },
                  { label: course?.title || 'Course', to: courseBuilderPath },
                  { label: 'Assignment Submissions' }
                ]
              : [
                  { label: 'Assignment Reviews', to: `${overviewPath}/assignments` },
                  { label: course?.title || 'Course', to: courseBuilderPath },
                  { label: module?.title || 'Module', to: moduleEditorPath },
                  { label: 'Assignment Submissions' }
                ]
          }
          actions={
            <button
              onClick={() => navigate(`${overviewPath}/assignments`)}
              className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-lg shadow-slate-200 dark:shadow-none"
            >
              <span className="material-symbols-outlined text-[18px]">arrow_back</span>
              Semua Tugas
            </button>
          }
        />

        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-violet-500 to-purple-600 text-white shadow-md">
            <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -mr-8 -mt-8"></div>
            <div className="flex items-center gap-3 mb-2">
              <div className="inline-flex items-center justify-center size-9 rounded-lg bg-white/20 backdrop-blur-md">
                <span className="material-symbols-outlined text-[20px]">assignment</span>
              </div>
              <p className="text-white/80 text-xs font-black uppercase tracking-wider">Total Submission</p>
            </div>
            <p className="text-2xl font-black">{allSubmissions.length}</p>
          </div>
          <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-md">
            <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -mr-8 -mt-8"></div>
            <div className="flex items-center gap-3 mb-2">
              <div className="inline-flex items-center justify-center size-9 rounded-lg bg-white/20 backdrop-blur-md">
                <span className="material-symbols-outlined text-[20px]">pending</span>
              </div>
              <p className="text-white/80 text-xs font-black uppercase tracking-wider">Perlu Review</p>
            </div>
            <p className="text-2xl font-black">{pendingSubmissions.length}</p>
          </div>
          <div className="relative overflow-hidden rounded-xl p-4 bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-md">
            <div className="absolute top-0 right-0 w-16 h-16 bg-white/10 rounded-full -mr-8 -mt-8"></div>
            <div className="flex items-center gap-3 mb-2">
              <div className="inline-flex items-center justify-center size-9 rounded-lg bg-white/20 backdrop-blur-md">
                <span className="material-symbols-outlined text-[20px]">check_circle</span>
              </div>
              <p className="text-white/80 text-xs font-black uppercase tracking-wider">Sudah Dinilai</p>
            </div>
            <p className="text-2xl font-black">{gradedSubmissions.length}</p>
          </div>
        </div>

        {/* Filter + Sort Bar */}
        <div className="bg-white dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-white/5 shadow-sm mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-1 bg-slate-100 dark:bg-white/5 p-1 rounded-xl">
              {[
                { value: 'all', label: 'Semua' },
                { value: 'submitted', label: 'Pending' },
                { value: 'graded', label: 'Dinilai' },
                { value: 'returned', label: 'Dikembalikan' },
              ].map((filter) => (
                <button
                  key={filter.value}
                  onClick={() => setStatusFilter(filter.value)}
                  className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${
                    statusFilter === filter.value
                      ? 'bg-slate-900 dark:bg-white text-white dark:text-slate-900 shadow-sm'
                      : 'text-slate-500 hover:text-slate-900 dark:hover:text-white'
                  }`}
                >
                  {filter.label}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-2">
              <span className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">Urutkan:</span>
              <select
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value as SortOption)}
                className="px-4 py-2 text-sm font-bold rounded-xl border border-slate-100 dark:border-white/10 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-primary/50 focus:ring-0 outline-none transition-all"
              >
                <option value="newest">Terbaru</option>
                <option value="oldest">Terlama</option>
                <option value="name_asc">Nama A-Z</option>
                <option value="name_desc">Nama Z-A</option>
              </select>
            </div>
          </div>
        </div>

        {/* Submissions List */}
        <div className="space-y-3">
          {filteredSubmissions.length > 0 ? (
            filteredSubmissions.map((submission) => (
              <SubmissionCard
                key={submission.id}
                submission={submission}
                onGrade={handleGrade}
                onDelete={handleDelete}
                onReturn={handleReturn}
                isPending={submission.status === 'submitted'}
                isDeleting={deleteSubmission.isPending}
              />
            ))
          ) : (
            <div className="rounded-2xl bg-white/50 dark:bg-slate-900/50 border border-dashed border-slate-200 dark:border-white/10 py-16 px-8 text-center backdrop-blur-sm">
              <div className="size-16 bg-slate-100 dark:bg-white/5 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="material-symbols-outlined text-[32px] text-slate-300 dark:text-slate-600">assignment</span>
              </div>
              <p className="text-slate-900 dark:text-white font-black text-lg mb-2">Belum Ada Submission</p>
              <p className="text-slate-500 dark:text-slate-400 text-sm font-bold">
                {statusFilter === 'all'
                  ? 'Belum ada siswa yang mengumpulkan tugas ini.'
                  : `Tidak ada submission dengan status ${statusFilter}.`
                }
              </p>
            </div>
          )}
        </div>

        {/* Grading Modal */}
        {showGradingModal && selectedSubmission && (
          <GradeSubmissionModal
            submission={selectedSubmission}
            maxScore={assignment.maxScore}
            onSave={handleSaveGrade}
            onClose={() => {
              setShowGradingModal(false)
              setSelectedSubmission(null)
            }}
            isSaving={gradeSubmission.isPending}
          />
        )}
      </div>
    )
  }

  // ========== LIST MODE ==========
  if (assignmentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="size-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-sm font-bold text-slate-500 dark:text-slate-400">Loading assignments...</p>
        </div>
      </div>
    )
  }

  // COURSE LIST VIEW
  if (viewMode === 'courses' || !selectedCourseId) {
    return (
      <div className="max-w-[1600px] mx-auto">
        <PageHeader
          title="Assignment Reviews"
          subtitle="Pilih kursus untuk melihat dan menilai submission tugas"
          breadcrumbs={
            isAdmin
              ? [{ label: 'Assignment Reviews' }]
              : [{ label: 'Assignment Reviews' }]
          }
        />

        {/* Search */}
        <div className="bg-white dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-white/5 shadow-sm mb-6">
          <div className="relative max-w-md">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[20px]">search</span>
            <input
              type="text"
              placeholder="Cari kursus..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent focus:border-primary/30 focus:bg-white rounded-2xl text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 outline-none transition-all"
            />
          </div>
        </div>

        {/* Course Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {coursesMap.length > 0 ? (
            coursesMap
              .filter((c) => !searchQuery || c.courseTitle.toLowerCase().includes(searchQuery.toLowerCase()))
              .map((courseItem) => (
                <div
                  key={courseItem.courseId}
                  onClick={() => {
                    setSelectedCourseId(courseItem.courseId)
                    setViewMode('assignments')
                    setSearchQuery('')
                  }}
                  className="group bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-white/5 p-5 hover:shadow-lg transition-all duration-300 cursor-pointer relative overflow-hidden"
                >
                  <div className="absolute top-0 right-0 w-16 h-16 bg-violet-500/5 rounded-full -mr-8 -mt-8 transition-all group-hover:scale-150"></div>
                  <div className="flex items-center gap-3 relative">
                    <div className="size-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white shadow-md shrink-0">
                      <span className="material-symbols-outlined text-[22px]">school</span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-black text-slate-900 dark:text-white text-lg group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors truncate">{courseItem.courseTitle}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 font-bold mt-1">
                        {courseItem.assignments.length} tugas
                      </p>
                    </div>
                    {courseItem.totalPending > 0 && (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-amber-500 text-white shrink-0">
                        <span className="material-symbols-outlined text-[14px]">pending</span>
                        {courseItem.totalPending} pending
                      </span>
                    )}
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-sm font-bold text-slate-400 group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">
                    <span>Lihat Tugas</span>
                    <span className="material-symbols-outlined text-[18px]">arrow_forward</span>
                  </div>
                </div>
              ))
          ) : (
            <div className="col-span-full text-center py-16 bg-white/50 dark:bg-slate-900/50 rounded-2xl border border-dashed border-slate-200 dark:border-white/10 backdrop-blur-sm">
              <div className="size-16 bg-slate-100 dark:bg-white/5 rounded-full mx-auto mb-4 flex items-center justify-center">
                <span className="material-symbols-outlined text-[40px] text-slate-300 dark:text-slate-600">school</span>
              </div>
              <p className="text-slate-900 dark:text-white font-black text-lg mb-2">Tidak Ada Kursus</p>
              <p className="text-slate-500 dark:text-slate-400 text-sm font-bold">Belum ada kursus dengan tugas yang dibuat.</p>
            </div>
          )}
        </div>
      </div>
    )
  }

  // ASSIGNMENT LIST VIEW
  return (
    <div className="max-w-[1600px] mx-auto">
      <PageHeader
        title="Assignment Reviews"
        subtitle={`Tugas dalam ${selectedCourseName}`}
        breadcrumbs={
          isAdmin
            ? [
                { label: 'Assignment Reviews', to: `${overviewPath}/assignments` },
                { label: selectedCourseName }
              ]
            : [
                { label: 'Assignment Reviews', to: `${overviewPath}/assignments` },
                { label: selectedCourseName }
              ]
        }
        actions={
          <button
            onClick={() => {
              setSelectedCourseId(null)
              setViewMode('courses')
              setSearchQuery('')
            }}
            className="flex items-center gap-2 px-5 py-2.5 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl font-bold text-sm hover:opacity-90 transition-all shadow-lg shadow-slate-200 dark:shadow-none"
          >
            <span className="material-symbols-outlined text-[18px]">arrow_back</span>
            Semua Kursus
          </button>
        }
      />

      {/* Search + Sort Bar */}
      <div className="bg-white dark:bg-slate-900/50 p-4 rounded-2xl border border-slate-100 dark:border-white/5 shadow-sm mb-6">
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
          <div className="relative flex-1 max-w-md">
            <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-[22px]">search</span>
            <input
              type="text"
              placeholder="Cari tugas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-transparent focus:border-primary/30 focus:bg-white rounded-2xl text-sm font-medium text-slate-900 dark:text-white placeholder-slate-400 outline-none transition-all"
            />
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-black text-slate-500 dark:text-slate-400 uppercase tracking-wider">Urutkan:</span>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value as SortOption)}
              className="px-4 py-2 text-sm font-bold rounded-xl border border-slate-100 dark:border-white/10 bg-white dark:bg-slate-900 text-slate-900 dark:text-white focus:border-primary/50 focus:ring-0 outline-none transition-all"
            >
              <option value="newest">Terbaru</option>
              <option value="oldest">Terlama</option>
              <option value="name_asc">Nama A-Z</option>
              <option value="name_desc">Nama Z-A</option>
            </select>
          </div>
        </div>
      </div>

      {/* Assignments List */}
      <div className="space-y-3">
        {courseAssignments.length > 0 ? (
          courseAssignments.map((assignmentItem) => (
            <div
              key={assignmentItem.id}
              onClick={() => navigate(`${overviewPath}/assignments/${assignmentItem.id}`)}
              className="group bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-white/5 p-5 hover:shadow-lg transition-all duration-300 cursor-pointer relative overflow-hidden"
            >
              <div className="absolute top-0 right-0 w-16 h-16 bg-violet-500/5 rounded-full -mr-8 -mt-8 transition-all group-hover:scale-150"></div>
              <div className="flex items-center justify-between gap-4 relative">
                <div className="flex items-center gap-3 flex-1 min-w-0">
                  <div className="size-10 rounded-xl bg-gradient-to-br from-violet-500 to-purple-600 flex items-center justify-center text-white shadow-md shrink-0">
                    <span className="material-symbols-outlined text-[22px]">assignment</span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-black text-slate-900 dark:text-white text-lg group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">{assignmentItem.title}</h3>
                      <p className="text-sm text-slate-500 dark:text-slate-400 font-bold mt-1">
                        {assignmentItem.moduleTitle} &rsaquo; {assignmentItem.lessonTitle}
                      </p>
                    </div>
                    {assignmentItem.isPublished ? (
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500 text-white">
                        <span className="material-symbols-outlined text-[14px]">check_circle</span>
                        Published
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-black rounded-lg bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10">
                        Draft
                      </span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-600 dark:text-slate-400 font-bold ml-[52px]">
                    <span className="flex items-center gap-1">
                      <span className="material-symbols-outlined text-[14px]">upload_file</span>
                      {assignmentItem.submissionCount} submission
                    </span>
                    {assignmentItem.pendingCount > 0 && (
                      <span className="flex items-center gap-1 text-amber-600 dark:text-amber-400">
                        <span className="material-symbols-outlined text-[14px]">pending</span>
                        {assignmentItem.pendingCount} pending
                      </span>
                    )}
                    <span className="text-slate-300 dark:text-slate-600">|</span>
                    <span>{assignmentItem.maxScore} pts</span>
                  </div>
                </div>
                <span className="material-symbols-outlined text-slate-300 dark:text-slate-600 text-[20px] group-hover:text-violet-600 dark:group-hover:text-violet-400 transition-colors">arrow_forward</span>
              </div>
          ))
        ) : (
          <div className="rounded-2xl bg-white/50 dark:bg-slate-900/50 border border-dashed border-slate-200 dark:border-white/10 py-16 px-8 text-center backdrop-blur-sm">
            <div className="size-16 bg-slate-100 dark:bg-white/5 rounded-full mx-auto mb-4 flex items-center justify-center">
              <span className="material-symbols-outlined text-[40px] text-slate-300 dark:text-slate-600">assignment</span>
            </div>
            <p className="text-slate-900 dark:text-white font-black text-lg mb-2">Tidak Ada Tugas</p>
            <p className="text-slate-500 dark:text-slate-400 text-sm font-bold">
              {searchQuery ? 'Tidak ada tugas yang sesuai dengan pencarian.' : 'Belum ada tugas di kursus ini.'}
            </p>
          </div>
        )}
      </div>
    </div>
  )
}

// ========== SUBMISSION CARD ==========
interface SubmissionCardProps {
  submission: AssignmentSubmission
  onGrade: (submission: AssignmentSubmission) => void
  onDelete: (submissionId: string) => void
  onReturn: (submissionId: string) => void
  isPending: boolean
  isDeleting: boolean
}

const SubmissionCard: React.FC<SubmissionCardProps> = ({ submission, onGrade, onDelete, onReturn, isPending, isDeleting }) => {
  const { t } = useTranslation('common')
  return (
    <div className="rounded-xl bg-white dark:bg-slate-900 border border-slate-100 dark:border-white/5 p-4 shadow-sm hover:shadow-md transition-all group">
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="size-9 rounded-xl bg-gradient-to-br from-slate-700 to-slate-900 flex items-center justify-center text-white font-black text-xs shadow-md shrink-0">
            {submission.studentName ? submission.studentName.charAt(0).toUpperCase() : 'S'}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-black text-slate-900 dark:text-white text-sm group-hover:text-primary transition-colors truncate">
              {submission.studentName || `${t('role.participant')} #${submission.enrollmentId.slice(0, 8)}`}
            </div>
            <div className="text-[11px] text-slate-500 dark:text-slate-400 font-bold truncate">
              {submission.studentEmail && <span>{submission.studentEmail} · </span>}
              {submission.submittedAt ? new Date(submission.submittedAt).toLocaleDateString('id-ID', { day: 'numeric', month: 'short', year: 'numeric' }) : 'N/A'}
            </div>
          </div>
          {submission.isLate && (
            <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-rose-500 text-white shrink-0">
              Terlambat
            </span>
          )}
          <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold shrink-0 ${
            submission.status === 'graded'
              ? 'bg-emerald-500 text-white'
              : submission.status === 'returned'
                ? 'bg-amber-500 text-white'
                : 'bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-slate-400 border border-slate-200 dark:border-white/10'
          }`}>
            {submission.status === 'graded' ? 'Dinilai' : submission.status === 'returned' ? 'Dikembalikan' : 'Pending'}
          </span>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          {isPending ? (
            <button
              onClick={() => onGrade(submission)}
              className="px-4 py-2 bg-slate-900 dark:bg-white text-white dark:text-slate-900 rounded-xl hover:opacity-90 transition-all text-xs font-black whitespace-nowrap shadow-lg shadow-slate-200 dark:shadow-none"
            >
              Nilai
            </button>
          ) : (
            <>
              <button
                onClick={() => onGrade(submission)}
                className="px-4 py-2 bg-slate-100 dark:bg-white/5 text-slate-900 dark:text-white rounded-xl hover:bg-slate-200 dark:hover:bg-white/10 transition-all text-xs font-bold whitespace-nowrap border border-slate-200 dark:border-white/10"
              >
                Detail
              </button>
              <button
                onClick={() => onReturn(submission.id)}
                className="px-2.5 py-1 bg-amber-500 text-white rounded-full text-xs font-bold whitespace-nowrap transition-all"
              >
                Kembali
              </button>
            </>
          )}
          {submission.fileUrl && (
            <a
              href={submission.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="px-4 py-2 bg-slate-100 dark:bg-white/5 text-slate-900 dark:text-white rounded-xl hover:bg-slate-200 dark:hover:bg-white/10 transition-all text-xs font-bold text-center whitespace-nowrap border border-slate-200 dark:border-white/10"
            >
              Download
            </a>
          )}
          <button
            onClick={() => onDelete(submission.id)}
            disabled={isDeleting}
            className="px-2.5 py-1 bg-rose-500 text-white rounded-full text-xs font-bold whitespace-nowrap transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isDeleting ? '...' : 'Hapus'}
          </button>
        </div>
      </div>
    </div>
  )
}

export default InstructorAssignmentSubmissionsPage
